import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Minus, Sparkles, Phone, Plus } from "lucide-react";
import DemoRequest from "../components/sections/DemoRequest";
import { useI18n } from "../i18n/I18nProvider";
import { useContent } from "../lib/ContentProvider";

const TIER_DEFS = [
  {
    id: "starter",
    monthly: 69,
    color: "#0284C7",
    features: [
      { key: "cloud_pos_1", on: true },
      { key: "unlimited_orders", on: true },
      { key: "receipts", on: true },
      { key: "sales_tax", on: true },
      { key: "refunds", on: true },
      { key: "twint_cards", on: true },
      { key: "email_chat", on: true },
      { key: "kds_addon", on: true },
      { key: "cds_addon", on: true },
      { key: "online_shop", on: false },
      { key: "waiter_handheld", on: false },
      { key: "kiosk", on: false },
    ],
  },
  {
    id: "grow",
    monthly: 89,
    color: "#7C3AED",
    features: [
      { key: "everything_starter", on: true },
      { key: "waiter_app", on: true },
      { key: "course_split", on: true },
      { key: "qr_pay", on: true },
      { key: "multi_3", on: true },
      { key: "loyalty_promos", on: true },
      { key: "phone_chat", on: true },
      { key: "kds_addon", on: true },
      { key: "cds_addon", on: true },
      { key: "online_shop_full", on: false },
      { key: "kiosk", on: false },
      { key: "multi_dash", on: false },
    ],
  },
  {
    id: "complete",
    monthly: 119,
    color: "#16A34A",
    featured: true,
    features: [
      { key: "everything_grow", on: true },
      { key: "online_shop_commission", on: true },
      { key: "multi_dash", on: true },
      { key: "multi_license_60", on: true },
      { key: "inventory_recipe", on: true },
      { key: "kds_addon", on: true },
      { key: "cds_addon", on: true },
      { key: "loyalty_gift", on: true },
      { key: "analytics", on: true },
      { key: "priority_onsite", on: true },
    ],
  },
];

const FREEDOM = {
  id: "freedom",
  monthly: 149,
  color: "#51dd7f",
  feature_keys: [
    "everything_complete", "kiosk_included", "kitchen_route", "kiosk_pay",
    "success_mgr", "custom_integrations", "qbr", "api_webhook", "free_kiosk_hw",
  ],
};

const PLAN_FEATURE_STRINGS_EN = {
  cloud_pos_1: "Cloud POS · 1 station",
  unlimited_orders: "Unlimited orders & products",
  receipts: "Receipts (paper + digital)",
  sales_tax: "Sales & tax reports",
  refunds: "Refunds & end-of-day Z-report",
  twint_cards: "TWINT, cards, contactless",
  email_chat: "Email + chat support",
  online_shop: "Online ordering / Online Shop",
  waiter_handheld: "Waiter App (handheld)",
  kiosk: "Self-Order Kiosk",
  everything_starter: "Everything in Starter",
  waiter_app: "Waiter App (iOS & Android)",
  course_split: "Course firing & split / merge checks",
  qr_pay: "QR Order & Pay (branded menus)",
  multi_3: "Multi-station sync (up to 3)",
  loyalty_promos: "Loyalty-ready promotions & vouchers",
  phone_chat: "Phone + chat support",
  online_shop_full: "Online Shop",
  multi_dash: "Multi-location dashboard",
  kds_addon: "Kitchen Display System (KDS) *",
  cds_addon: "Customer Display System (CDS) *",
  everything_grow: "Everything in Grow",
  online_shop_commission: "Online Shop (commission-free)",
  multi_license_60: "Up to 60% off for multi-license",
  inventory_recipe: "Inventory + recipe / cost control",
  kds: "Kitchen Display System (KDS)",
  cds: "Customer Display System (CDS)",
  loyalty_gift: "Gift Cards & Loyalty memberships",
  analytics: "Advanced analytics & exports",
  priority_onsite: "Priority phone + chat + on-site within 48h",
  everything_complete: "Everything in Complete",
  kiosk_included: "Self-Order Kiosk (23.8\") included",
  kitchen_route: "Direct-to-kitchen ticket routing",
  kiosk_pay: "TWINT, Apple Pay, Google Pay on kiosk",
  success_mgr: "Dedicated success manager",
  custom_integrations: "Custom integrations on request",
  qbr: "Quarterly business review",
  api_webhook: "API & webhook access",
  free_kiosk_hw: "Free kiosk hardware on eligibility (CHF 20K+/mo card sales)",
};

const PLAN_FEATURE_STRINGS_FR = {
  cloud_pos_1: "Caisse cloud · 1 poste",
  unlimited_orders: "Commandes & produits illimités",
  receipts: "Tickets (papier + numérique)",
  sales_tax: "Rapports de ventes & TVA",
  refunds: "Remboursements & rapport Z de fin de journée",
  twint_cards: "TWINT, cartes, sans contact",
  email_chat: "Support e-mail + chat",
  online_shop: "Commande en ligne / Boutique",
  waiter_handheld: "App serveur (mobile)",
  kiosk: "Borne en libre-service",
  everything_starter: "Tout Starter",
  waiter_app: "App serveur (iOS & Android)",
  course_split: "Envoi par plats & division / fusion des additions",
  qr_pay: "Commande & paiement QR (menus brandés)",
  multi_3: "Synchro multi-postes (jusqu'à 3)",
  loyalty_promos: "Promos & bons cadeau",
  phone_chat: "Support téléphone + chat",
  online_shop_full: "Boutique en ligne",
  multi_dash: "Tableau de bord multi-sites",
  kds_addon: "Kitchen Display System (KDS) *",
  cds_addon: "Customer Display System (CDS) *",
  everything_grow: "Tout Grow",
  online_shop_commission: "Boutique en ligne (sans commission)",
  multi_license_60: "Jusqu'à 60 % de remise multi-licences",
  inventory_recipe: "Stocks + recettes / coût matière",
  kds: "Kitchen Display System (KDS)",
  cds: "Customer Display System (CDS)",
  loyalty_gift: "Cartes cadeaux & adhésions fidélité",
  analytics: "Analytique avancée & exports",
  priority_onsite: "Téléphone + chat prioritaires + intervention sous 48 h",
  everything_complete: "Tout Complete",
  kiosk_included: "Borne 23,8\" incluse",
  kitchen_route: "Routage direct vers la cuisine",
  kiosk_pay: "TWINT, Apple Pay, Google Pay sur borne",
  success_mgr: "Customer Success Manager dédié",
  custom_integrations: "Intégrations sur mesure à la demande",
  qbr: "Revue trimestrielle d'activité",
  api_webhook: "Accès API & webhooks",
  free_kiosk_hw: "Matériel borne offert sous éligibilité (CHF 20K+/mois de ventes carte)",
};

function priceFor(monthly, withTerminal, yearly, tierId) {
  // Freedom is a fixed bundle price — the terminal discount does NOT apply.
  const fixed = tierId === "freedom";
  const adjusted = withTerminal && !fixed ? Math.max(0, monthly - 30) : monthly;
  return yearly ? adjusted * 10 : adjusted;
}

export default function Pricing() {
  const { t, locale } = useI18n();
  const { content } = useContent();
  const FEATURES = locale === "fr" ? PLAN_FEATURE_STRINGS_FR : PLAN_FEATURE_STRINGS_EN;
  const [withTerminal, setWithTerminal] = React.useState(true);
  const [yearly, setYearly] = React.useState(false);
  const planPrice = (id, fallback) => content?.pricing?.plans?.find((p) => p.id === id)?.monthly ?? fallback;
  const kioskPrice = content?.pricing?.kiosk_addon_price ?? 1980;
  const freedomMonthly = planPrice("freedom", FREEDOM.monthly);

  const cardRate = withTerminal ? "0.90%" : "1.5%";
  const TIERS = TIER_DEFS.map((tier) => ({
    ...tier,
    monthly: planPrice(tier.id, tier.monthly),
    name: t(`pricing_page.${tier.id}_name`),
    blurb: t(`pricing_page.${tier.id}_blurb`),
    cta: t(`pricing_page.${tier.id}_cta`),
    features: tier.features.map((f) => ({ ...f, label: FEATURES[f.key] })),
  }));

  return (
    <div data-testid="pricing-page">
      {/* Hero */}
      <section className="py-24 sm:py-32 border-b border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("pricing_page.eyebrow")}</div>
          <h1 className="font-display mt-4 text-balance text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] text-oj-ink max-w-4xl">
            {t("pricing_page.h1_a")}<br />{t("pricing_page.h1_b")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-600 leading-relaxed">
            {t("pricing_page.intro_a")} <span className="font-semibold text-oj-ink">{cardRate}</span>{" "}
            {withTerminal ? t("pricing_page.intro_b_with") : t("pricing_page.intro_b_without")}.
          </p>
        </div>
      </section>

      {/* Free hardware banner */}
      <section data-testid="free-hardware-banner" className="bg-oj-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 noise opacity-30 pointer-events-none" />
        <div className="container-oj relative py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-oj-brand/40 bg-oj-brand/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-oj-brand">
                <Sparkles className="size-3.5" /> {t("pricing_page.banner_eligible")}
              </div>
              <h2 className="font-display mt-5 text-balance text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                {t("pricing_page.banner_h2_a")} <span className="bg-oj-brand text-zinc-950 px-3 rounded-md">{t("pricing_page.banner_h2_amount")}</span>{t("pricing_page.banner_h2_z")}
              </h2>
              <p className="mt-5 max-w-xl text-lg text-zinc-300 leading-relaxed">
                {t("pricing_page.banner_body")}{" "}
                <span className="font-semibold text-white">{t("pricing_page.banner_rate_phrase")}</span>.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm">
                {[t("pricing_page.banner_pt1"), t("pricing_page.banner_pt2"), t("pricing_page.banner_pt3")].map((tx) => (
                  <li key={tx} className="flex items-start gap-2 text-zinc-200">
                    <Check className="size-4 text-oj-brand mt-0.5 shrink-0" />
                    {tx}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/#demo" data-testid="free-hardware-cta" className="inline-flex items-center gap-1.5 bg-oj-brand text-zinc-950 font-semibold px-6 py-4 rounded-md hover:opacity-90 transition-opacity">
                  {t("pricing_page.banner_check")} <ArrowUpRight className="size-5" />
                </Link>
                <Link to="/callback" className="inline-flex items-center gap-1.5 border border-white/20 bg-white/5 text-white font-semibold px-6 py-4 rounded-md hover:bg-white/10 transition-colors">
                  <Phone className="size-4" /> {t("pricing_page.banner_callback")}
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
                <img
                  src="/images/pos-bundle.png"
                  alt="O'javana POS bundle: dual-screen POS, cash drawer, receipt printer, payment terminal"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                  /pos-bundle
                </div>
                <div className="absolute bottom-4 right-4 bg-oj-brand text-zinc-950 px-2.5 py-1 rounded font-mono text-[11px] font-bold uppercase tracking-wider">
                  CHF 0
                </div>
              </div>
              <div className="mt-3 text-center text-xs text-zinc-500">{t("pricing_page.banner_bundle_caption")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Big + connector */}
      <section className="relative bg-zinc-50" data-testid="plus-divider">
        <div className="container-oj py-10 flex items-center justify-center">
          <div className="flex items-center gap-4 sm:gap-6 w-full max-w-2xl">
            <div className="flex-1 h-px bg-zinc-300" />
            <div className="relative">
              <div className="size-16 sm:size-20 rounded-full bg-oj-ink text-white grid place-items-center shadow-xl">
                <Plus className="size-7 sm:size-9" strokeWidth={3} />
              </div>
              <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 whitespace-nowrap">
                {t("pricing_page.hw_sw_label")}
              </span>
            </div>
            <div className="flex-1 h-px bg-zinc-300" />
          </div>
        </div>
      </section>

      {/* Toggles */}
      <section className="bg-zinc-50 pt-2 pb-10" data-testid="pricing-toggles">
        <div className="container-oj">
          <div className="mt-6 flex flex-wrap items-center justify-center gap-8 gap-y-4">
            <Toggle
              testid="toggle-terminal"
              label={t("pricing_page.toggle_terminal")}
              caption={t("pricing_page.toggle_terminal_caption")}
              checked={withTerminal}
              onChange={() => setWithTerminal((v) => !v)}
              activeColor="#51dd7f"
            />
            <div className="hidden sm:block h-10 w-px bg-zinc-300" />
            <Toggle
              testid="toggle-yearly"
              label={t("pricing_page.toggle_yearly")}
              caption={t("pricing_page.toggle_yearly_caption")}
              checked={yearly}
              onChange={() => setYearly((v) => !v)}
              activeColor="#0284C7"
            />
          </div>
        </div>
      </section>

      {/* Plan grid (3 tiers) */}
      <section className="pt-2 pb-12 bg-zinc-50">
        <div className="container-oj">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {TIERS.map((tier) => {
              const price = priceFor(tier.monthly, withTerminal, yearly, tier.id);
              return (
                <article
                  key={tier.id}
                  data-testid={`pricing-tier-${tier.id}`}
                  className={`relative rounded-2xl bg-white p-7 flex flex-col ${
                    tier.featured ? "border-2 border-oj-kiosk shadow-xl lg:scale-[1.02]" : "border border-zinc-200"
                  }`}
                >
                  {tier.featured && (
                    <span className="absolute -top-3 left-6 text-[11px] font-bold uppercase tracking-[0.2em] bg-oj-kiosk text-white px-2 py-1 rounded">
                      {t("pricing_page.most_chosen")}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: tier.color }} />
                    <h3 className="font-display text-2xl font-bold tracking-tight text-oj-ink">{tier.name}</h3>
                  </div>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="text-sm font-semibold text-zinc-500">CHF</span>
                    <span
                      data-testid={`pricing-price-${tier.id}`}
                      className="font-display text-5xl font-black tracking-tighter text-oj-ink"
                    >
                      {price}
                    </span>
                    <span className="text-zinc-500 text-sm">{yearly ? t("pricing_page.per_yr") : t("pricing_page.per_mo")} {t("pricing_page.per_station")}</span>
                  </div>
                  {withTerminal && (
                    <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-oj-kiosk">
                      {t("pricing_page.with_term_save")}
                    </div>
                  )}
                  {yearly && (
                    <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-oj-shop">
                      {t("pricing_page.two_months_free")}
                    </div>
                  )}
                  <p className="mt-3 text-zinc-700 text-sm leading-relaxed">{tier.blurb}</p>

                  <Link
                    to="/#demo"
                    data-testid={`pricing-cta-${tier.id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-1.5 w-full font-semibold px-5 py-3.5 rounded-md transition-colors ${
                      tier.featured ? "bg-oj-ink text-white hover:bg-zinc-800" : "border border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {tier.cta} <ArrowUpRight className="size-4" />
                  </Link>

                  <ul className="mt-7 space-y-2.5 text-sm flex-1">
                    {tier.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2 ${f.on ? "text-zinc-800" : "text-zinc-400"}`}>
                        {f.on ? (
                          <Check className="size-4 mt-0.5 shrink-0" style={{ color: tier.color }} />
                        ) : (
                          <Minus className="size-4 mt-0.5 shrink-0 text-zinc-300" />
                        )}
                        <span className={f.on ? "" : "line-through decoration-zinc-300"}>{f.label}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>

          {/* Freedom — separate row */}
          <article
            data-testid="pricing-tier-freedom"
            className="mt-8 relative rounded-2xl bg-oj-ink text-white p-8 lg:p-10 overflow-hidden border-2 border-oj-payment"
          >
            <div className="absolute inset-0 noise opacity-30 pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: FREEDOM.color }} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-oj-payment">{t("pricing_page.premium_kiosk_led")}</span>
                </div>
                <h3 className="font-display mt-3 text-4xl sm:text-5xl font-black tracking-tighter">{t("pricing_page.freedom_name")}</h3>
                <p className="mt-3 text-zinc-300 max-w-lg">{t("pricing_page.freedom_blurb")}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {FREEDOM.feature_keys.map((k) => (
                    <li key={k} className="flex items-start gap-2 text-zinc-100">
                      <Check className="size-4 mt-0.5 shrink-0 text-oj-payment" />
                      {FEATURES[k]}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-2xl bg-white text-oj-ink p-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t("pricing_page.freedom_meta")}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-sm font-semibold text-zinc-500">CHF</span>
                    <span data-testid="pricing-price-freedom" className="font-display text-6xl font-black tracking-tighter">
                      {priceFor(freedomMonthly, withTerminal, yearly, FREEDOM.id)}
                    </span>
                    <span className="text-zinc-500 text-sm">{yearly ? t("pricing_page.per_yr") : t("pricing_page.per_mo")} {t("pricing_page.per_station")}</span>
                  </div>
                  {withTerminal && <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-zinc-500">{t("pricing_page.freedom_fixed")}</div>}
                  {yearly && <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-oj-shop">{t("pricing_page.two_months_free")}</div>}
                  <div className="mt-4 text-sm text-zinc-700">
                    {t("pricing_page.freedom_kiosk_note")}
                  </div>
                  <Link
                    to="/#demo"
                    data-testid="pricing-cta-freedom"
                    className="mt-5 inline-flex items-center justify-center gap-1.5 w-full bg-oj-ink text-white font-semibold px-5 py-3.5 rounded-md hover:bg-zinc-800"
                  >
                    {t("pricing_page.freedom_cta")} <ArrowUpRight className="size-4" />
                  </Link>
                </div>
              </div>
            </div>
          </article>

          <p className="mt-8 text-sm text-zinc-500 text-center max-w-3xl mx-auto">
            {t("pricing_page.plan_includes")} <span className="font-semibold text-oj-ink">CHF 1,980</span> {t("pricing_page.plan_includes_unit")}
          </p>
          <p className="mt-3 text-sm text-zinc-500 text-center max-w-3xl mx-auto" data-testid="kds-cds-footnote">
            {t("pricing_page.kds_cds_footnote")}
          </p>
        </div>
      </section>

      {/* Hardware add-on */}
      <section className="py-20 bg-white border-t border-zinc-200" data-testid="hardware-addon">
        <div className="container-oj grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("pricing_page.addon_label")}</div>
            <h2 className="font-display mt-4 text-balance text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] text-oj-ink">
              {t("pricing_page.addon_h2_a")} <span className="text-oj-kiosk">CHF {Number(kioskPrice).toLocaleString("de-CH")}</span>
            </h2>
            <p className="mt-5 max-w-xl text-lg text-zinc-600">
              {t("pricing_page.addon_body")}
            </p>
            <ul className="mt-6 space-y-2.5 text-zinc-800">
              {[
                t("pricing_page.addon_p1"),
                t("pricing_page.addon_p2"),
                t("pricing_page.addon_p3"),
                t("pricing_page.addon_p4"),
                t("pricing_page.addon_p5"),
              ].map((b) => (
                <li key={b} className="flex items-start gap-2"><Check className="size-4 mt-0.5 shrink-0 text-oj-kiosk" /> {b}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/configurator" data-testid="hardware-configure-cta" className="inline-flex items-center gap-1.5 bg-oj-ink text-white font-semibold px-6 py-3.5 rounded-md hover:bg-zinc-800">
                {t("pricing_page.addon_cta_cfg")} <ArrowUpRight className="size-4" />
              </Link>
              <Link to="/solutions/kiosk" className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-zinc-900 text-oj-ink font-semibold px-6 py-3.5 rounded-md">
                {t("pricing_page.addon_cta_kiosk")}
              </Link>
            </div>
          </div>
          <div className="relative rounded-2xl border border-zinc-200 overflow-hidden">
            <img
              src="/images/kiosk-neuchatel.jpg"
              alt="O'javana Self-Order Kiosk 23.8"
              className="w-full h-[520px] object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <DemoRequest />
    </div>
  );
}

function Toggle({ testid, label, caption, checked, onChange, activeColor }) {
  return (
    <label data-testid={testid} className="inline-flex items-center gap-3 cursor-pointer select-none">
      <span
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
          checked ? "" : "bg-zinc-300"
        }`}
        style={checked ? { background: activeColor } : {}}
      >
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={onChange}
          data-testid={`${testid}-input`}
        />
        <span
          className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </span>
      <span className="flex flex-col">
        <span className="text-sm font-semibold text-oj-ink">{label}</span>
        <span className="text-[11px] text-zinc-500">{caption}</span>
      </span>
    </label>
  );
}
